
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-soundcharts-purple text-white hover:bg-soundcharts-deepPurple",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-soundcharts-purple/10 hover:text-soundcharts-purple",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-soundcharts-purple/10 hover:text-soundcharts-purple",
        link: "text-soundcharts-purple underline-offset-4 hover:underline",
        soundcharts: "bg-soundcharts-gradient text-white border-0 shadow-soundcharts hover:shadow-neon",
        "soundcharts-outline": "bg-transparent text-soundcharts-purple border border-soundcharts-purple/50 shadow-soundcharts hover:shadow-neon hover:bg-soundcharts-purple/10",
        "soundcharts-ghost": "text-soundcharts-purple hover:bg-soundcharts-purple/10",
        neon: "bg-black/50 text-neon-blue border border-neon-blue/50 shadow-neon-blue hover:shadow-neon-blue hover:bg-black/70 hover:border-neon-blue",
        "neon-purple": "bg-black/50 text-neon-purple border border-neon-purple/50 shadow-neon-purple hover:shadow-neon-purple hover:bg-black/70 hover:border-neon-purple",
        "neon-pink": "bg-black/50 text-neon-pink border border-neon-pink/50 shadow-neon-pink hover:shadow-neon-pink hover:bg-black/70 hover:border-neon-pink",
        "neon-green": "bg-black/50 text-neon-green border border-neon-green/50 shadow-neon-green hover:shadow-neon-green hover:bg-black/70 hover:border-neon-green",
        "neon-yellow": "bg-black/50 text-neon-yellow border border-neon-yellow/50 shadow-neon-yellow hover:shadow-neon-yellow hover:bg-black/70 hover:border-neon-yellow",
        cyber: "bg-gradient-to-r from-neon-blue to-neon-purple text-white border-0 hover:from-neon-purple hover:to-neon-blue transition-colors duration-500",
        glass: "bg-white/5 backdrop-blur-sm border border-white/20 text-white hover:bg-white/10 transition-all duration-300",
        "glass-neon": "bg-black/30 backdrop-blur-sm border border-neon-purple/20 text-white shadow-neon-purple/10 hover:border-neon-purple/40 hover:shadow-neon-purple/20 transition-all duration-300",
        "holographic": "relative bg-black/40 backdrop-blur-sm border border-white/20 text-white overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent before:animate-shine hover:border-white/30",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
